import { defineStore } from 'pinia';
import { getEntryUrlParamValue, isIphonex } from '@shared/utils';

interface GlobalState {
  // 企业应用信息
  appData: {
    currentCorpId: string;
    agentId: string;
    appId?: string;
    authCorpId?: string;
    suiteId?: string;
  };
  firstEntryPageUrl: string; // 初始访问页面 url，主要用于微信/企业微信签名
  firstEntryStaffId?: string; // 初始访问页面的员工 staffId
  isManagerView: boolean; // 是否经理视图
  isAllowProtocol: boolean; // 用户授权隐私协议状态：true 表示同意，false 表示不同意
  isShowSwitchBank: boolean;
  /**
   * sourceType页面来源:
      messagePush:推送
        1:url包含messagePushType:
          messagePushType=2,群发任务列表推送
          messagePushType=welcome,欢迎推送
          messagePushType=updateTarget,更新下月目标推送
        2:url包含templateId，策略模板推送,
      workbench:工作台
   */
  sourceType: 'workbench' | 'messagePush' | 'single_chat_tools';
  isIphoneX: boolean; // 是否是iPhone X
  agentIdMap: Map<string, string>;
  cardIsShow: boolean; // 个人名片信息switch是否打开
  newRadar: boolean; // 是否显示新雷达
  hasAccountPermission: boolean; // 是否有账号权限
}

export const useGlobalStore = defineStore({
  id: 'global',
  state: (): GlobalState => {
    return {
      // 企业应用信息
      appData: {
        currentCorpId: getEntryUrlParamValue('currentCorpId'),
        agentId: getEntryUrlParamValue('agentId'),
        appId: '',
        authCorpId: '',
      },
      firstEntryPageUrl: '', // 初始访问页面 url
      firstEntryStaffId: getEntryUrlParamValue('staffId'), // 初始访问页面的员工 staffId
      isManagerView: false, // 是否经理视图
      isAllowProtocol: true, // 用户授权隐私协议状态
      isShowSwitchBank: false, // 白名单银行支持分享时控制经理名片与顶部的展示
      sourceType: 'workbench', // 来源标识，workbench--工作台，pushMessage--推送消息，single_chat_tools--侧边栏1V1
      isIphoneX: false, // 是否iPhone X
      agentIdMap: new Map(),
      cardIsShow: true,
      newRadar: false,
      hasAccountPermission: true, // 默认有账号权限
    };
  },
  getters: {},
  actions: {
    /**
     * 更新企业应用信息
     * @param data {object} 企业应用信息
     * @param data.appId {string}
     * @param data.authCorpId {string}
     */
    async setAppData({ appId = '', authCorpId = '' }) {
      this.appData = {
        ...this.appData,
        appId,
        authCorpId,
      };
    },

    /**
     * 更新初始访问页面 url
     */
    async setFirstEntryPageUrl() {
      this.firstEntryPageUrl = location.href;
    },

    /**
     * 更新初始访问页面的员工 staffId
     * @param staffId {string} 员工 id
     */
    async setFirstEntryStaffId(staffId = '') {
      this.firstEntryStaffId = staffId;
    },

    /**
     * 更新经理视图标识
     * @param staffId {string} 员工 id
     */
    async setIsManagerView(staffId = '') {
      this.isManagerView = this.firstEntryStaffId === staffId;
    },

    /**
     * 更新授权隐私协议状态
     * @param isAgree {string} 授权协议状态：true 表示同意，false 表示不同意
     */
    async setIsAllowProtocol(isAgree: boolean) {
      this.isAllowProtocol = isAgree;
    },

    /**
     * 更新页面来源
     */
    setSourceType(type?: 'single_chat_tools' | 'workbench' | 'messagePush') {
      const search = location.search;
      if (search.includes('messagePushType=') || search.includes('templateId=')) {
        this.sourceType = 'messagePush';
      }
      if (type) {
        this.sourceType = type;
      }
    },

    setIsIphonex() {
      this.isIphoneX = isIphonex();
    },
    setAgentIdMap({ key, value }: { key: string; value: string }) {
      this.agentIdMap.set(key, value);
    },
    /**
     * 更新账号权限
     * @param hasPermission {boolean} 当前登录用户是否有账号权限：true 有，false 无
     */
    setHasAccountPermission(hasPermission: boolean) {
      this.hasAccountPermission = hasPermission;
    },
  },
});
